import { ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react';
import ReactCropperImage from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import { getCroppedImg } from '@components/drag-and-drop/src/utils';
import { SwitchToggle } from '@components/switch-toggle';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';

interface Props {
  image: {
    file: File | null;
    src: string;
  };
  open: boolean;
  setOpen: (value: boolean) => void;
  onCancelCrop: () => void;
  onSaveCrop: (image: { file: File; src: string; isSquare: boolean }) => void;
  translations: {
    cancel: string;
    save: string;
    zoom: string;
    rotate: string;
  };
}

interface Area {
  width: number;
  height: number;
  x: number;
  y: number;
}

export const Cropper = ({ open, setOpen, image: { file, src }, onSaveCrop, onCancelCrop, translations }: Props) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isSquareAspectRatio, setIsSquareAspectRatio] = useState(true);
  const [currentAspectRatio, setCurrentAspectRatio] = useState(1);

  // Reset aspect ratio when modal opens - always start with 1:1
  useEffect(() => {
    if (open) {
      setIsSquareAspectRatio(true);
      setCurrentAspectRatio(1);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
    }
  }, [open]);

  // Update aspect ratio when toggle changes
  useEffect(() => {
    setCurrentAspectRatio(isSquareAspectRatio ? 1 : 16 / 9);
    // Reset crop when aspect ratio changes
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  }, [isSquareAspectRatio]);

  // TODO: check unknown type
  const onCropComplete = useCallback((_: unknown, area: Area) => {
    // eslint-disable-next-line
    setCroppedAreaPixels(area);
  }, []);

  const saveCroppedImage = async () => {
    if (!file) {
      return;
    }
    const { type: imgType } = file;
    if (!croppedAreaPixels) {
      return;
    }

    const croppedImage = await getCroppedImg(
      src,
      // eslint-disable-next-line
      imgType,
      croppedAreaPixels,
      rotation
    );
    const isSquare = Math.abs(currentAspectRatio - 1) < 0.01;
    onSaveCrop({ ...(croppedImage as { file: File; src: string }), isSquare });
  };

  const handleZoom = (e: ChangeEvent<HTMLInputElement>) => {
    setZoom(Number.parseFloat(e.target.value));
  };

  const handleRotate = (e: ChangeEvent<HTMLInputElement>) => {
    setRotation(Number.parseFloat(e.target.value));
  };

  return (
    <Transition show={open} as={Fragment} appear={true}>
      <Dialog className='relative z-50' open={open} onClose={setOpen}>
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 backdrop-overlay transition-opacity' aria-hidden='true' />
        </TransitionChild>

        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <DialogPanel className='bg-surface-primary rounded-lg w-full max-w-2xl shadow-xl transform transition-all'>
              <div className='px-4 pt-5 pb-4 sm:p-6'>
                <div className='relative w-full h-96'>
                  <ReactCropperImage
                    image={src}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={currentAspectRatio}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                <div className='mt-4 sm:mt-6'>
                  <div className='flex items-center justify-between mb-4'>
                    <div className='flex items-center gap-3'>
                      <SwitchToggle
                        active={isSquareAspectRatio}
                        loading={false}
                        onToggleSwitch={(value) => setIsSquareAspectRatio(value)}
                        disabled={false}
                      />
                      <div className='flex flex-col'>
                        <span className='text-secondary text-subtitle-s'>{t('eventInfo:coverAspectRatioTitle')}</span>
                        <span className='text-tertiary text-body-xs'>{t('eventInfo:coverAspectRatioDescription')}</span>
                      </div>
                    </div>
                  </div>
                  <div className='grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-y-4 gap-x-8'>
                    <div className='flex flex-row space-x-4'>
                      <label className='text-primary' htmlFor='zoom'>
                        {translations.zoom}
                      </label>
                      <input
                        id='zoom'
                        name='zoom'
                        className='text-primary'
                        value={zoom}
                        type='range'
                        onChange={handleZoom}
                        min='1'
                        max='3'
                        step='0.1'
                      />
                    </div>

                    <div className='flex flex-row space-x-4'>
                      <label className='text-primary' htmlFor='rotate'>
                        {translations.rotate}
                      </label>
                      <input
                        id='rotate'
                        name='rotate'
                        value={rotation}
                        type='range'
                        onChange={handleRotate}
                        min={0}
                        max={360}
                        step={1}
                      />
                    </div>
                  </div>

                  <div className='flex justify-end mt-4 sm:mt-6 gap-4'>
                    <ButtonSecondary type='button' data-testid='cancel-event-image' onClick={onCancelCrop}>
                      {translations.cancel}
                    </ButtonSecondary>

                    <ButtonPrimary type='button' data-testid='save-event-image' onClick={saveCroppedImage}>
                      {translations.save}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
